import React from 'react';
import { Clock, RotateCcw, Users } from 'lucide-react';
import useScrollFadeIn from '../hooks/useScrollFadeIn';

const problems = [
  {
    icon: Clock,
    title: 'Delayed Replies Cost You Customers',
    description:
      'Every minute a customer waits on WhatsApp, the chance of losing them grows. Most businesses take hours, or even days, to reply.',
  },
  {
    icon: RotateCcw,
    title: 'Repetitive Questions Drain Your Time',
    description:
      'Your team answers the same 20 questions over and over. Pricing, hours, location, availability. It never ends.',
  },
  {
    icon: Users,
    title: 'Hiring More Staff Is Expensive',
    description:
      'Adding support agents costs money you may not have. And scaling customer service is never as simple as hiring one more person.',
  },
];

export default function Problem() {
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <section
      id="problem"
      className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 relative"
      style={{ background: 'var(--bg-section-1)' }}
    >
      <div
        ref={ref}
        className={`max-w-6xl mx-auto transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Section heading */}
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-heading)' }}>
            Sound Familiar?
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-body)' }}>
            These are the biggest pain points businesses face with WhatsApp customer service.
          </p>
        </div>

        {/* Problem cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <div
                key={index}
                className="rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  boxShadow: 'var(--shadow-card)',
                  backdropFilter: 'blur(var(--glass-blur))',
                  WebkitBackdropFilter: 'blur(var(--glass-blur))',
                  transitionDelay: `${index * 100}ms`,
                }}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ background: 'var(--bg-icon)' }}
                >
                  <Icon className="w-7 h-7" style={{ color: 'var(--text-accent)' }} />
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-heading)' }}>
                  {problem.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-body)' }}>
                  {problem.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
